import React, { useCallback } from 'react'
import { useConfig } from 'msp-integrations'
import { MinusSmIcon, PlusSmIcon } from '@heroicons/react/outline'
import debounce from 'lodash/debounce'
import * as S from './ProductQuantity.styles'

const DEBOUNCE_INTERVAL = 500

interface ProductQuantityProps {
  productId: string
  selectedQuantity: number
  limit?: number
  disabled?: boolean
  withDebounce?: boolean
  width?: string
  onChange?: (quantity: number) => void
}

const ProductQuantity = ({
  productId,
  limit = 10,
  disabled = false,
  withDebounce = false,
  selectedQuantity,
  onChange
}: ProductQuantityProps) => {
  limit = limit === 0 ? 1000 : limit
  const {
    theme: { font }
  } = useConfig()
  const increaseQuantity = (quantity: number) => {
    quantity < limit && !disabled && onChange && onChange(quantity + 1)
  }

  const decreaseQuantity = (quantity: number) => {
    quantity > 1 && !disabled && onChange && onChange(quantity - 1)
  }

  const handleQuantityIncrease = useCallback(
    debounce(increaseQuantity, DEBOUNCE_INTERVAL, {
      leading: true,
      trailing: false,
      maxWait: DEBOUNCE_INTERVAL
    }),
    []
  )

  const handleQuantityDecrease = useCallback(
    debounce(decreaseQuantity, DEBOUNCE_INTERVAL, {
      leading: true,
      trailing: false,
      maxWait: DEBOUNCE_INTERVAL
    }),
    []
  )

  return (
    <div id={`quantity-selector-${productId}`} className={S.Wrapper}>
      <MinusSmIcon
        onClick={() =>
          withDebounce
            ? handleQuantityDecrease(selectedQuantity)
            : decreaseQuantity(selectedQuantity)
        }
        className={`${
          selectedQuantity === 1 || disabled ? S.IconDisabled : S.IconClickable
        }`}
      />
      <div className={`${font.regular} ${S.QuantityDisplay}`}>
        {selectedQuantity}
      </div>
      <PlusSmIcon
        onClick={() =>
          withDebounce
            ? handleQuantityIncrease(selectedQuantity)
            : increaseQuantity(selectedQuantity)
        }
        className={`${
          selectedQuantity === limit || disabled
            ? S.IconDisabled
            : S.IconClickable
        }`}
      />
    </div>
  )
}

export default ProductQuantity
